import type { Event, RecipientGroup, RSVP } from '@/types';
import { shouldIncludeRsvpInUnpaidAnnouncementGroup } from '@/lib/event-utils';

export function buildDefaultAnnouncementSubject(eventName?: string): string {
  return `Update: ${eventName?.trim() || 'Event'}`;
}

export function getDiningPublicBaseUrl(): string {
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return import.meta.env.MODE === 'production' ? 'https://dining.masonic-tools.co.uk' : 'http://localhost:3000';
}

export function buildAnnouncementRsvpUrl(unitId: string, eventId: string): string {
  return `${getDiningPublicBaseUrl()}/unit/${unitId}/event/${eventId}/rsvp`;
}

/** Logged-in guest dashboard (all RSVPs across events). */
export function buildAnnouncementRsvpDashboardUrl(): string {
  return `${getDiningPublicBaseUrl()}/dashboard`;
}

export function buildAnnouncementPaymentUrl(invoiceId: string): string {
  return `${getDiningPublicBaseUrl()}/payment/${invoiceId}`;
}

export function formatEventDateForAnnouncementEmail(event: Event | null): string {
  if (!event?.date) {
    return 'TBC';
  }
  try {
    return new Date(event.date).toLocaleDateString('en-GB', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return 'TBC';
  }
}

export type PreviewExampleSource = 'rsvp' | 'placeholder';

export interface PreviewExampleRecipient {
  name: string;
  email: string;
  source: PreviewExampleSource;
}

/**
 * Picks one RSVP that would receive the email for the current send-to selection, for a realistic preview.
 */
export function getPreviewExampleRecipient(
  rsvps: RSVP[],
  sendTo: RecipientGroup[],
  event: Event | null,
  invoiceById: Map<string, { status?: string; outstandingBalance?: number }>
): PreviewExampleRecipient {
  const placeholder = (): PreviewExampleRecipient => ({
    name: 'W. Bro. Example Guest',
    email: 'guest@example.com',
    source: 'placeholder',
  });

  if (rsvps.length === 0) {
    return placeholder();
  }

  const firstWithEmail = (list: RSVP[]): RSVP | undefined => list.find((r) => r.email?.trim());

  if (sendTo.length === 0) {
    const hit = firstWithEmail(rsvps);
    return hit
      ? { name: hit.name?.trim() || 'Guest', email: hit.email, source: 'rsvp' }
      : placeholder();
  }

  if (sendTo.includes('all')) {
    const hit = firstWithEmail(rsvps);
    return hit
      ? { name: hit.name?.trim() || 'Guest', email: hit.email, source: 'rsvp' }
      : placeholder();
  }

  for (const group of sendTo) {
    let candidates: RSVP[] = [];
    switch (group) {
      case 'confirmed':
        candidates = rsvps.filter((r) => r.status === 'confirmed');
        break;
      case 'waitlist':
        candidates = rsvps.filter((r) => r.status === 'waitlist');
        break;
      case 'unpaid':
        candidates = rsvps.filter((r) =>
          shouldIncludeRsvpInUnpaidAnnouncementGroup(r, event, invoiceById)
        );
        break;
      case 'subscribers':
      case 'unit-members':
        continue;
      default:
        break;
    }
    const hit = firstWithEmail(candidates);
    if (hit) {
      return { name: hit.name?.trim() || 'Guest', email: hit.email, source: 'rsvp' };
    }
  }

  if (sendTo.includes('subscribers') || sendTo.includes('unit-members')) {
    return {
      name: 'Example member or guest',
      email: 'member@example.com',
      source: 'placeholder',
    };
  }

  return placeholder();
}

export interface PaymentPreviewSample {
  formattedBalance: string;
  paymentUrl: string;
}

/**
 * Uses the first invoice with money outstanding (not pending verification) for a realistic payment block preview.
 */
export function getPaymentPreviewSample(
  rsvps: RSVP[],
  invoiceById: Map<string, { status?: string; outstandingBalance?: number }>
): PaymentPreviewSample | null {
  for (const r of rsvps) {
    if (!r.invoiceId) {
      continue;
    }
    const inv = invoiceById.get(r.invoiceId);
    if (!inv || inv.status === 'pending_verification') {
      continue;
    }
    const bal = inv.outstandingBalance ?? 0;
    if (bal > 0) {
      const formattedBalance = new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
      }).format(bal);
      return {
        formattedBalance,
        paymentUrl: buildAnnouncementPaymentUrl(r.invoiceId),
      };
    }
  }
  return null;
}
